import { Link } from 'react-router-dom'

export default function Footer() {
  const currentYear = new Date().getFullYear()

  const quickLinks = [
    { label: 'Forside', href: '/' },
    { label: 'Om mig', href: '/om-mig' },
    { label: 'Psykoterapi', href: '/psykoterapi' },
    { label: 'Ydelser', href: '/ydelser' },
    { label: 'Priser & praktisk', href: '/priser' },
    { label: 'Kontakt', href: '/kontakt' }
  ]

  const legalLinks = [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Cookie Policy (EU)', href: '/cookie-policy-eu' }
  ]

  return (
    <footer className="bg-brand-dark text-brand-offwhite pt-16 pb-8 border-t border-brand-dark/20">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
        {/* Brand Column */}
        <div className="flex flex-col gap-4">
          <Link to="/" className="flex flex-col group inline-block" aria-label="Terapien.dk - Forside">
            <span className="font-serif text-3xl font-bold tracking-tight text-brand-offwhite group-hover:text-brand-sand transition-colors flex items-center gap-1">
              Terapien<span className="text-brand-sand">.dk</span>
              <span className="inline-block w-2 h-2 rounded-full bg-brand-sand ml-0.5" />
            </span>
            <span className="text-xs uppercase tracking-[0.2em] text-brand-sand/80 font-semibold mt-0.5">
              v/ Birgitte Vilsgaard
            </span>
          </Link>
          <p className="text-sm text-brand-offwhite/70 leading-relaxed mt-2">
            Terapien.dk v/ Birgitte Vilsgaard – Psykoterapeut i Svendborg. Individuel psykoterapi, parterapi, familieterapi og supervision for hele Fyn.
          </p>
          <div className="mt-4 pt-2">
            <a
              href="https://www.facebook.com/GardeVilsgaard"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Følg Terapien.dk på Facebook"
              className="inline-flex items-center gap-2 text-xs font-medium text-brand-sand hover:text-brand-offwhite transition-colors duration-200"
            >
              <i className="fa-brands fa-facebook-f text-sm" aria-hidden="true" />
              <span>Følg os på Facebook</span>
            </a>
          </div>
        </div>

        {/* Quick Links Column */}
        <div>
          <h3 className="font-serif text-lg font-semibold text-brand-sand mb-6">Genveje</h3>
          <ul className="grid grid-cols-2 gap-3">
            {quickLinks.map((link, idx) => (
              <li key={idx}>
                <Link
                  to={link.href}
                  className="text-sm text-brand-offwhite/70 hover:text-brand-sand transition-colors duration-150"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Opening Hours Column */}
        <div>
          <h3 className="font-serif text-lg font-semibold text-brand-sand mb-6">Åbningstider</h3>
          <ul className="space-y-3 text-sm text-brand-offwhite/70">
            <li className="flex justify-between border-b border-brand-offwhite/10 pb-1.5">
              <span>Tirsdag – torsdag</span>
              <span className="font-medium text-brand-sand">08.00 – 17.00</span>
            </li>
            <li className="flex justify-between border-b border-brand-offwhite/10 pb-1.5">
              <span>Fredag</span>
              <span className="font-medium text-brand-sand">08.00 – 15.00</span>
            </li>
            <li className="flex justify-between items-start gap-2 pb-1.5">
              <span>Weekender</span>
              <span className="text-brand-sand font-medium text-right text-xs sm:text-sm">Kan aftales individuelt</span>
            </li>
          </ul>
        </div>

        {/* Contact Info Column */}
        <div>
          <h3 className="font-serif text-lg font-semibold text-brand-sand mb-6">Kontaktoplysninger</h3>
          <ul className="space-y-4 text-sm text-brand-offwhite/70">
            <li className="flex items-start gap-3">
              <i className="fa-solid fa-phone text-brand-sand mt-0.5" />
              <div>
                <span className="block text-xs uppercase tracking-wider text-brand-sage">Ring i dag</span>
                <a href="tel:28839488" className="hover:text-brand-sand font-medium text-brand-offwhite whitespace-nowrap">28 83 94 88</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <i className="fa-solid fa-envelope text-brand-sand mt-0.5" />
              <div>
                <span className="block text-xs uppercase tracking-wider text-brand-sage">Send en mail</span>
                <a href="mailto:bgv@terapien.dk" className="hover:text-brand-sand font-medium text-brand-offwhite break-all">bgv@terapien.dk</a>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <i className="fa-solid fa-location-dot text-brand-sand mt-0.5" />
              <div>
                <span className="block text-xs uppercase tracking-wider text-brand-sage">Find klinikken</span>
                <span className="block text-brand-offwhite font-medium">Dronningemaen 68</span>
                <span className="block">5700 Svendborg</span>
              </div>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="max-w-7xl mx-auto px-6 pt-8 border-t border-brand-offwhite/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-brand-offwhite/50">
        <div>
          <p>© {currentYear} Terapien.dk v/ Birgitte Vilsgaard. Alle rettigheder forbeholdes.</p>
          <p className="mt-1">Dronningemaen 68, 5700 Svendborg · CVR: 43779362</p>
        </div>
        <div className="flex items-center gap-6">
          <a
            href="https://www.facebook.com/GardeVilsgaard"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            className="hover:text-brand-sand transition-colors duration-150 flex items-center gap-1.5"
          >
            <i className="fa-brands fa-facebook-f text-sm" aria-hidden="true" />
            <span>Facebook</span>
          </a>
          {legalLinks.map((link, idx) => (
            <Link
              key={idx}
              to={link.href}
              className="hover:text-brand-sand transition-colors duration-150"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  )
}
